<script setup lang="ts">
import { ref, watch } from 'vue'
import { X, Pencil, Trash2 } from 'lucide-vue-next'
import { AppButton, type PaletteHue } from '@/design-system/components'
import { lessonsApi, type LessonSlot, type RescheduleScope } from '@/api'

/** Manage the class's recurring weekly slots — reschedule (whole series or
 *  this-and-following) or delete, on the slot API. */
const props = defineProps<{ modelValue: boolean; classId: string; hue: PaletteHue }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; changed: [] }>()

const DAYS = [
  { value: 'MONDAY', label: 'Mon' },
  { value: 'TUESDAY', label: 'Tue' },
  { value: 'WEDNESDAY', label: 'Wed' },
  { value: 'THURSDAY', label: 'Thu' },
  { value: 'FRIDAY', label: 'Fri' },
  { value: 'SATURDAY', label: 'Sat' },
  { value: 'SUNDAY', label: 'Sun' },
]
function dayLabel(v: string) {
  return DAYS.find((d) => d.value === v)?.label ?? v
}
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

const slots = ref<LessonSlot[]>([])
const loading = ref(true)
const busy = ref(false)

// Edit state
const editingId = ref<string | null>(null)
const editDay = ref('MONDAY')
const editStart = ref('09:00')
const editEnd = ref('10:00')
const editRoom = ref('')
const editScope = ref<RescheduleScope>('ALL')
const editFrom = ref('')

// Delete state
const deletingId = ref<string | null>(null)
const keepPast = ref(true)

async function load() {
  loading.value = true
  try {
    slots.value = await lessonsApi.getLessonSlots(props.classId)
  } catch {
    slots.value = []
  } finally {
    loading.value = false
  }
}
watch(() => props.modelValue, (open) => {
  if (open) {
    editingId.value = null
    deletingId.value = null
    load()
  }
})

function startEdit(s: LessonSlot) {
  deletingId.value = null
  editingId.value = s.id
  editDay.value = s.dayOfWeek
  editStart.value = s.startTime
  editEnd.value = s.endTime
  editRoom.value = s.room ?? ''
  editScope.value = 'ALL'
  editFrom.value = s.seriesStart
}
async function applyEdit(s: LessonSlot) {
  if (busy.value) return
  busy.value = true
  try {
    await lessonsApi.rescheduleSlot(s.id, {
      scope: editScope.value,
      fromDate: editScope.value === 'FUTURE' ? editFrom.value : undefined,
      rule: {
        dayOfWeek: editDay.value,
        startTime: editStart.value,
        endTime: editEnd.value,
        room: editRoom.value.trim() || undefined,
      },
    })
    editingId.value = null
    emit('changed')
    await load()
  } catch (e) {
    console.error('Failed to reschedule slot:', e)
  } finally {
    busy.value = false
  }
}
async function confirmDelete(s: LessonSlot) {
  if (busy.value) return
  busy.value = true
  try {
    await lessonsApi.deleteLessonSlot(s.id, keepPast.value)
    deletingId.value = null
    emit('changed')
    await load()
  } catch (e) {
    console.error('Failed to delete slot:', e)
  } finally {
    busy.value = false
  }
}
function close() {
  emit('update:modelValue', false)
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="msm">
      <div class="msm__backdrop" @click="close" />
      <div class="msm__panel">
        <header class="msm__head">
          <h2 class="msm__title">Manage schedule</h2>
          <button type="button" class="msm__x" aria-label="Close" @click="close"><X :size="18" /></button>
        </header>

        <div class="msm__body">
          <div v-if="loading" class="msm__muted">Loading…</div>
          <div v-else-if="!slots.length" class="msm__muted">
            No recurring lessons yet. Add a weekly repeat from the schedule.
          </div>

          <div v-for="s in slots" :key="s.id" class="msm__slot">
            <!-- summary row -->
            <div v-if="editingId !== s.id && deletingId !== s.id" class="msm__row">
              <span class="msm__day" :style="{ background: hue.soft, color: hue.ink }">{{ dayLabel(s.dayOfWeek) }}</span>
              <div class="msm__info">
                <div class="msm__time">{{ s.startTime }}–{{ s.endTime }}<template v-if="s.room"> · {{ s.room }}</template></div>
                <div class="msm__range">{{ fmtDate(s.seriesStart) }} → {{ fmtDate(s.seriesEnd) }}<template v-if="s.title"> · {{ s.title }}</template></div>
              </div>
              <button type="button" class="msm__ibtn" aria-label="Edit" @click="startEdit(s)"><Pencil :size="15" /></button>
              <button type="button" class="msm__ibtn msm__ibtn--danger" aria-label="Delete" @click="deletingId = s.id; editingId = null"><Trash2 :size="15" /></button>
            </div>

            <!-- edit form -->
            <div v-else-if="editingId === s.id" class="msm__edit">
              <div class="msm__editgrid">
                <select v-model="editDay" class="msm__input">
                  <option v-for="d in DAYS" :key="d.value" :value="d.value">{{ d.label }}</option>
                </select>
                <input v-model="editStart" type="time" class="msm__input" />
                <input v-model="editEnd" type="time" class="msm__input" />
                <input v-model="editRoom" type="text" class="msm__input" placeholder="Room" />
              </div>
              <div class="msm__scope">
                <label><input v-model="editScope" type="radio" value="ALL" /> Whole series</label>
                <label><input v-model="editScope" type="radio" value="FUTURE" /> This &amp; following</label>
                <input v-if="editScope === 'FUTURE'" v-model="editFrom" type="date" class="msm__input msm__input--from" />
              </div>
              <div class="msm__editactions">
                <AppButton variant="ghost" size="sm" @click="editingId = null">Cancel</AppButton>
                <AppButton variant="primary" size="sm" :loading="busy" @click="applyEdit(s)">Apply</AppButton>
              </div>
            </div>

            <!-- delete confirm -->
            <div v-else class="msm__delete">
              <p class="msm__delq">Delete this weekly {{ dayLabel(s.dayOfWeek) }} {{ s.startTime }} slot?</p>
              <label class="msm__keep"><input v-model="keepPast" type="checkbox" /> Keep past lessons as one-offs</label>
              <div class="msm__editactions">
                <AppButton variant="ghost" size="sm" @click="deletingId = null">Cancel</AppButton>
                <AppButton variant="danger" size="sm" :loading="busy" @click="confirmDelete(s)">Delete</AppButton>
              </div>
            </div>
          </div>
        </div>

        <footer class="msm__foot">
          <AppButton variant="secondary" size="md" @click="close">Done</AppButton>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.msm {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: var(--font-sans);
}
.msm__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}
.msm__panel {
  position: relative;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-elevated, 0 10px 40px rgba(0, 0, 0, 0.2));
  overflow: hidden;
}
.msm__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--outline-variant);
}
.msm__title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--text-primary);
}
.msm__x {
  border: 0;
  background: transparent;
  color: var(--ink-400);
  cursor: pointer;
  padding: 4px;
  border-radius: var(--radius-sm);
}
.msm__x:hover {
  color: var(--text-primary);
  background: var(--ink-100);
}
.msm__body {
  padding: 12px 20px;
  overflow-y: auto;
}
.msm__muted {
  padding: 24px 0;
  text-align: center;
  font-size: 13.5px;
  color: var(--ink-400);
}
.msm__slot {
  border-bottom: 1px solid var(--outline-variant);
  padding: 10px 0;
}
.msm__slot:last-child {
  border-bottom: 0;
}
.msm__row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.msm__day {
  font-size: 11.5px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  text-transform: uppercase;
}
.msm__info {
  flex: 1;
  min-width: 0;
}
.msm__time {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
.msm__range {
  font-size: 12px;
  color: var(--ink-500);
  margin-top: 1px;
}
.msm__ibtn {
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: var(--ink-400);
  cursor: pointer;
  padding: 6px;
  border-radius: var(--radius-sm);
}
.msm__ibtn:hover {
  color: var(--text-primary);
  background: var(--ink-100);
}
.msm__ibtn--danger:hover {
  color: var(--danger-ink);
  background: var(--danger-soft);
}
.msm__edit,
.msm__delete {
  padding: 4px 0;
}
.msm__editgrid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr;
  gap: 6px;
}
.msm__input {
  width: 100%;
  padding: 7px 8px;
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: 13px;
  color: var(--text-primary);
  background: var(--surface);
}
.msm__scope {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 10px;
  font-size: 12.5px;
  color: var(--ink-600);
}
.msm__scope label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}
.msm__input--from {
  width: auto;
  padding: 5px 8px;
}
.msm__editactions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.msm__delq {
  margin: 0 0 8px;
  font-size: 13.5px;
  color: var(--text-primary);
}
.msm__keep {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--ink-600);
  cursor: pointer;
}
.msm__foot {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-top: 1px solid var(--outline-variant);
}
</style>
